/**
 * 需求来源：docs/pod页面改版/AI提示词.md 第1次提交第1次补充 —— /printonDemand/home 迁移为 React SSR 页。
 * 注意事项：
 *  1. 按参考图 docs/pod页面改版/pod.html 14 区块 1:1 还原，文案走 i18next(见 constants.js)，图片为本地资源。
 *  2. 用户决策：不保留 productList/trendingList 真实商品模块，品类 Tab 示例数据硬编码。
 *  3. 交互(hero 轮播 / 视频切换 / 品类 Tab / FAQ 手风琴)用 React hooks 还原参考图原生 JS 行为。
 *  4. 样式复用 cj-pod-* 全局类(index.less 以 :global 包裹)，故此处用字面量 className。
 *  5. withSSR 包装导出，Component.fetch = fetchData，路由见 app/ssrRoutes.js。
 */
import React, { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import withSSR from '../../../hoc/withSSR';
import fetchData from './fetch';
import { getConstants } from './constants';
import './index.less';

/**
 * 第1次提交第5/6次补充：商品详情页地址。
 * 第6次补充明确「参考 mf-components/packages/mf-mycj-components/.../ProductCard/index.tsx 的实现」，
 * 该卡片 anchorDetail 调 common/index.ts 的 getProductHref(id, name)=`/product/${urlQueryFormat(name)}-p-${id}.html`，
 * urlQueryFormat：小写→去 , & ' ? →空格转 - →encodeURIComponent。此处原样复刻，保持与商品行情卡片一致。
 * @param {string|number} id 商品 id
 * @param {string} name 商品英文名
 * @returns {string} 商品详情页相对地址
 */
function getProductDetailHref(id, name) {
  var _url = String(name || '').toLocaleLowerCase();
  _url = _url.replace(/,/g, '').replace(/( )?&/g, '').replace(/'/g, '').replace(/\?/g, '').replace(/ /g, '-');
  return '/product/' + encodeURIComponent(_url) + '-p-' + id + '.html';
}

/**
 * 第1次提交第4次补充：按钮跳转目标。
 * - Explore POD Products → /PrintonDemand.html
 * - Start Print on Demand / Start POD with CJ → 注册页 /register.html
 * - Submit a Custom Request → /my.html#/pod/design-myself
 * - 商品卡片(Custom Design/View/整卡)→ 商品详情页（参考 product.njk 的 GOODSDETAIL_LINK→getProductHref 拼接）
 */
const EXPLORE_HREF = '/PrintonDemand.html';
const REGISTER_HREF = '/register.html';
const CUSTOM_REQUEST_HREF = '/my.html#/pod/design-myself';

// ========== 1. HERO ==========
function HeroSection({ HERO }) {
  const [index, setIndex] = useState(0);
  const timerRef = useRef(null);
  const slideCount = HERO.slides.length;

  const go = useCallback((i) => setIndex(((i % slideCount) + slideCount) % slideCount), [slideCount]);

  const startAuto = useCallback(() => {
    stopAuto();
    timerRef.current = setInterval(() => setIndex((p) => (p + 1) % slideCount), 4300);
  }, [slideCount]);
  const stopAuto = () => { if (timerRef.current) clearInterval(timerRef.current); };

  useEffect(() => { startAuto(); return stopAuto; }, [startAuto]);

  return (
    <section className="cj-pod-hero-creative">
      <div className="cj-pod-orange-blur cj-blur-1" />
      <div className="cj-pod-orange-blur cj-blur-2" />
      <div className="cj-pod-hero-wrap">
        <div className="cj-pod-hero-content">
          <div className="cj-pod-label"><span />{HERO.label}</div>
          <h1>{HERO.title}</h1>
          <p>{HERO.desc}</p>
          <div className="cj-pod-points">
            {HERO.points.map((t, i) => <span key={i}>{t}</span>)}
          </div>
          <div className="cj-pod-actions">
            <a target="_blank" rel="noopener noreferrer" href={REGISTER_HREF} className="cj-pod-btn cj-pod-btn-main" data-gtag-element="hero_start_pod">{HERO.btnStart}</a>
            <a target="_blank" rel="noopener noreferrer" href={EXPLORE_HREF} className="cj-pod-btn cj-pod-btn-light" data-gtag-element="hero_explore_pod">{HERO.btnExplore}</a>
          </div>
          <div className="cj-pod-review-box">
            <div className="cj-pod-review-item">
              <div className="cj-pod-review-title">{HERO.trustpilot.name}</div>
              <div className="cj-pod-review-main">
                <strong>4.7</strong>
                <div className="cj-pod-trust-stars" aria-label="Trustpilot rating 4.7 out of 5">
                  <span>★</span><span>★</span><span>★</span><span>★</span><span className="partial">★</span>
                </div>
              </div>
              <p>{HERO.trustpilot.reviews}</p>
            </div>
            <div className="cj-pod-review-divider" />
            <div className="cj-pod-review-item">
              <div className="cj-pod-review-title">{HERO.shopify.name}</div>
              <div className="cj-pod-review-main">
                <strong>4.9</strong>
                <div className="cj-pod-shopify-stars" aria-label="Shopify rating 4.9 out of 5">
                  <span>★</span><span>★</span><span>★</span><span>★</span><span>★</span>
                </div>
              </div>
              <p>{HERO.shopify.reviews}</p>
            </div>
          </div>
        </div>

        <div className="cj-pod-hero-visual">
          <div className="cj-pod-showcase" onMouseEnter={stopAuto} onMouseLeave={startAuto}>
            <div className="cj-pod-showcase-frame">
              <div className="cj-pod-showcase-track" style={{ transform: `translateX(-${index * 100}%)` }}>
                {HERO.slides.map((s, i) => (
                  <div className="cj-pod-case-slide" key={i}>
                    <div className="cj-pod-case-media">
                      <img src={s.img} alt={s.tag} />
                      <div className="cj-pod-case-tag">{s.tag}</div>
                    </div>
                    <div className="cj-pod-case-content">
                      <span>{s.span}</span>
                      <h4>{s.title}</h4>
                      <p>{s.desc}</p>
                      <div className="cj-pod-case-meta">
                        {s.meta.map((m, mi) => (
                          <div key={mi}><strong>{m.t}</strong><small>{m.s}</small></div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="cj-pod-thumb-row">
              {HERO.slides.map((s, i) => (
                <button key={i} className={'cj-pod-thumb' + (i === index ? ' active' : '')} type="button" onClick={() => { go(i); startAuto(); }} aria-label={'Show ' + s.thumb}>
                  <img src={s.img} alt={s.thumb} /><span>{s.thumb}</span>
                </button>
              ))}
            </div>
            <div className="cj-pod-showcase-footer">
              {HERO.footer.map((f, i) => (
                <div key={i}><strong>{f.t}</strong><span>{f.s}</span></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ========== 2. INTRO + VIDEO ==========
function IntroSection({ INTRO }) {
  const [active, setActive] = useState(0);
  const videoRef = useRef(null);
  const tab = INTRO.videoTabs[active];

  useEffect(() => { if (videoRef.current) videoRef.current.load(); }, [active]);

  return (
    <section className="cj-pod-intro-module">
      <div className="cj-pod-section-head">
        <span>{INTRO.label}</span>
        <h2>{INTRO.h2}</h2>
      </div>
      <div className="cj-pod-intro-wrap">
        <div className="cj-pod-intro-left">
          <h3>{INTRO.h3}</h3>
          <p>{INTRO.p1}</p>
          <p>{INTRO.p2}</p>
          <div className="cj-pod-intro-steps">
            {INTRO.steps.map((s, i) => (
              <div key={i}><span>{'0' + (i + 1)}</span><strong>{s}</strong></div>
            ))}
          </div>
          <div className="cj-pod-text-note">
            <strong>{INTRO.noteT}</strong>
            <span>{INTRO.noteS}</span>
          </div>
        </div>
        <div className="cj-pod-intro-right">
          <div className="cj-pod-video-card">
            <div className="cj-pod-video-switch">
              {INTRO.videoTabs.map((t, i) => (
                <button key={i} type="button" className={'cj-pod-video-tab' + (i === active ? ' active' : '')} onClick={() => setActive(i)}>
                  <span>{t.tag}</span><strong>{t.title}</strong>
                </button>
              ))}
            </div>
            <div className="cj-pod-video-frame">
              <video ref={videoRef} className="cj-pod-video-player" src={tab.src} title={tab.title} controls playsInline preload="metadata" />
            </div>
            <div className="cj-pod-video-note">
              <strong className="cj-pod-video-dynamic-title">{tab.title}</strong>
              <span className="cj-pod-video-dynamic-note">{tab.note}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ========== 3. DIFFERENCE ==========
function DiffSection({ DIFF }) {
  return (
    <section className="cj-pod-diff-alt-section">
      <div className="cj-pod-diff-alt-bg bg-1" />
      <div className="cj-pod-diff-alt-bg bg-2" />
      <div className="cj-pod-diff-alt-container">
        <div className="cj-pod-diff-alt-head">
          <span className="cj-pod-diff-alt-label">{DIFF.label}</span>
          <h2>{DIFF.h2}</h2>
          <p>{DIFF.desc}</p>
        </div>
        {DIFF.rows.map((r, i) => (
          <div className={'cj-pod-diff-row' + (i % 2 === 1 ? ' reverse' : '')} key={i}>
            <div className="cj-pod-diff-image"><img src={r.img} alt={r.h3} /></div>
            <div className="cj-pod-diff-content">
              <span className="cj-pod-diff-number">{r.num}</span>
              <h3>{r.h3}</h3>
              <p>{r.p}</p>
              <ul>{r.li.map((li, li2) => <li key={li2}>{li}</li>)}</ul>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ========== 4. CATEGORY ==========
// 第1次提交第4次补充：类目+商品来自接口（fetch.js 预取），商品卡片整卡/Custom Design/View 跳商品详情。
function CategorySection({ CATEGORY, categories }) {
  const [active, setActive] = useState(0);
  const tabsRef = useRef(null);
  const list = categories || [];
  const cat = list[active] || { name: '', products: [] };
  const products = (cat.products || []).slice(0, 10);

  const scroll = (delta) => { if (tabsRef.current) tabsRef.current.scrollBy({ left: delta, behavior: 'smooth' }); };

  return (
    <section className="cj-pod-category-section">
      <div className="cj-pod-category-bg cj-pod-category-bg-1" />
      <div className="cj-pod-category-bg cj-pod-category-bg-2" />
      <div className="cj-pod-category-container">
        <div className="cj-pod-category-head">
          <span className="cj-pod-category-label">{CATEGORY.label}</span>
          <h2>{CATEGORY.h2}</h2>
          <p>{CATEGORY.desc}</p>
        </div>
        <div className="cj-pod-category-tabs-wrap">
          <button className="cj-pod-tab-arrow cj-pod-tab-prev" type="button" aria-label="Previous categories" onClick={() => scroll(-360)}>‹</button>
          <div className="cj-pod-category-tabs" ref={tabsRef}>
            {list.map((c, i) => (
              <button key={c.id || i} type="button" className={'cj-pod-category-tab' + (i === active ? ' active' : '')} onClick={() => setActive(i)}>{c.name}</button>
            ))}
          </div>
          <button className="cj-pod-tab-arrow cj-pod-tab-next" type="button" aria-label="Next categories" onClick={() => scroll(360)}>›</button>
        </div>
        <div className="cj-pod-product-panel">
          <div className="cj-pod-product-panel-top">
            <div>
              <span id="cjPodCategoryTag">{cat.name}</span>
              <h3 id="cjPodCategoryTitle">{cat.name + ' POD Product'}</h3>
            </div>
            <a target="_blank" rel="noopener noreferrer" href={EXPLORE_HREF} className="cj-pod-category-btn" data-gtag-element="category_explore">{CATEGORY.btn}</a>
          </div>
          <div className="cj-pod-product-grid">
            {products.map((p, i) => {
              // 商品详情页地址：复刻 mf-components ProductCard 的 getProductHref(id, name)
              const detailHref = getProductDetailHref(p.id, p.name);
              return (
                <a target="_blank" rel="noopener noreferrer" className="cj-pod-product-card" key={p.id || i} href={detailHref} data-gtag-element="category_product_card">
                  <div className="cj-pod-product-img">
                    <img src={p.image} alt={p.name} loading="lazy" />
                    <div className="cj-pod-product-badge">POD Ready</div>
                  </div>
                  <div className="cj-pod-product-body">
                    <div className="cj-pod-product-title">{p.name}</div>
                    <div className="cj-pod-product-mini">
                      <span>Custom Design</span>
                      <span className="cj-pod-product-view">View</span>
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

// ========== 5. BENEFITS ==========
function BenefitsSection({ BENEFITS }) {
  return (
    <section className="cj-pod-benefits-section">
      <div className="cj-pod-benefits-container">
        <div className="cj-pod-benefits-head">
          <span className="cj-pod-benefits-label">{BENEFITS.label}</span>
          <h2>{BENEFITS.h2}</h2>
          <p>{BENEFITS.desc}</p>
        </div>
        <div className="cj-pod-benefits-grid">
          {BENEFITS.cards.map((c, i) => (
            <div className="cj-pod-benefit-card" key={i}>
              <div className="cj-pod-benefit-img"><img src={c.img} alt={c.h3} /></div>
              <div className="cj-pod-benefit-content">
                <div className="cj-pod-benefit-top"><span>{c.num}</span><em>{c.em}</em></div>
                <h3>{c.h3}</h3><p>{c.p}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ========== 6. SERVICES ==========
function ServicesSection({ SERVICES }) {
  return (
    <section className="cj-pod-services-section">
      <div className="cj-pod-services-bg cj-pod-services-bg-1" />
      <div className="cj-pod-services-bg cj-pod-services-bg-2" />
      <div className="cj-pod-services-container">
        <div className="cj-pod-services-visual">
          <div className="cj-pod-services-image-card">
            <img src={SERVICES.image} alt="CJdropshipping print on demand services" />
            {SERVICES.floats.map((f, i) => (
              <div className={'cj-pod-services-floating cj-pod-float-' + (i + 1)} key={i}><span>{f}</span></div>
            ))}
          </div>
        </div>
        <div className="cj-pod-services-content">
          <span className="cj-pod-services-label">{SERVICES.label}</span>
          <h2>{SERVICES.h2}</h2>
          <p className="cj-pod-services-desc">{SERVICES.desc}</p>
          <div className="cj-pod-services-list">
            {SERVICES.items.map((it, i) => (
              <div className="cj-pod-service-item" key={i}>
                <div className="cj-pod-service-icon">{it.num}</div>
                <div><h3>{it.h3}</h3><p>{it.p}</p></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ========== 7. BRAND ==========
function BrandSection({ BRAND }) {
  return (
    <section className="cj-pod-brand-section">
      <div className="cj-pod-brand-bg cj-pod-brand-bg-1" />
      <div className="cj-pod-brand-bg cj-pod-brand-bg-2" />
      <div className="cj-pod-brand-container">
        <div className="cj-pod-brand-content">
          <span className="cj-pod-brand-label">{BRAND.label}</span>
          <h2>{BRAND.h2}</h2>
          <p>{BRAND.desc}</p>
          <div className="cj-pod-brand-list">
            {BRAND.list.map((b, i) => (
              <div className="cj-pod-brand-item" key={i}>
                <span>{b.num}</span>
                <div><strong>{b.t}</strong><p>{b.p}</p></div>
              </div>
            ))}
          </div>
        </div>
        <div className="cj-pod-brand-visual">
          <div className="cj-pod-package-stage">
            <div className="cj-pod-package-main">
              <img src={BRAND.image} alt="Custom packaging for ecommerce brand" />
              <div className="cj-pod-package-overlay">
                <strong>{BRAND.overlay.t}</strong>
                <span>{BRAND.overlay.s}</span>
              </div>
            </div>
            {BRAND.cards.map((c, i) => (
              <div className={'cj-pod-package-card ' + c.cls} key={i}>
                <div className={'cj-pod-package-icon' + (c.blue ? ' blue' : '')}>{c.icon}</div>
                <div><strong>{c.t}</strong><span>{c.s}</span></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ========== 8. PROCESS ==========
function ProcessSection({ PROCESS }) {
  return (
    <section className="cj-pod-process-section">
      <div className="cj-pod-process-bg cj-pod-process-bg-1" />
      <div className="cj-pod-process-bg cj-pod-process-bg-2" />
      <div className="cj-pod-process-container">
        <div className="cj-pod-process-head">
          <span className="cj-pod-process-label">{PROCESS.label}</span>
          <h2>{PROCESS.h2}</h2>
          <p>{PROCESS.desc}</p>
        </div>
        <div className="cj-pod-process-flow">
          {PROCESS.steps.map((s, i) => (
            <React.Fragment key={i}>
              <div className="cj-pod-process-card">
                <div className="cj-pod-process-icon">{s.icon}</div>
                <div className="cj-pod-process-step">{s.step}</div>
                <h3>{s.h3}</h3>
                <p>{s.p}</p>
              </div>
              {i < PROCESS.steps.length - 1 ? <div className="cj-pod-process-arrow"><span /></div> : null}
            </React.Fragment>
          ))}
        </div>
        <div className="cj-pod-process-bottom">
          {PROCESS.bottom.map((b, i) => (
            <div key={i}><strong>{b.t}</strong><span>{b.s}</span></div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ========== 9. NETWORK ==========
// 第1次提交第14次补充：中心 hub 展示站点 favicon；平台节点改用 OSS /resources/platform/ 真实平台图（移除原雪碧图白卡与装饰点）。
// 第1次提交第22次补充：中心 hub 由 favicon 改展示 `${ossHost}/config-resource/${siteName.toLowerCase()}/chat-logo.svg`（chatLogo 由父组件拼好传入）。
function NetworkSection({ NETWORK, chatLogo }) {
  const linePaths = [
    'M550 215 C455 160, 270 90, 80 70', 'M550 215 C465 135, 330 58, 245 45', 'M550 215 C500 140, 430 65, 385 70',
    'M550 215 C590 135, 650 62, 715 58', 'M550 215 C635 135, 790 58, 875 45', 'M550 215 C700 145, 900 90, 1010 78',
    'M550 215 C430 210, 245 200, 105 200', 'M550 215 C680 210, 865 205, 1002 205', 'M550 215 C420 260, 240 310, 120 328',
    'M550 215 C460 285, 330 355, 255 372', 'M550 215 C550 290, 550 350, 550 374', 'M550 215 C650 285, 760 350, 835 372',
    'M550 215 C710 285, 910 320, 1012 330'
  ];
  return (
    <section className="cj-pod-network-logo-section">
      <div className="cj-pod-network-bg" />
      <div className="cj-pod-network-container">
        <div className="cj-pod-network-head">
          <h2>{NETWORK.h2}</h2>
          <p>{NETWORK.desc}</p>
        </div>
        <div className="cj-pod-network-map">
          <svg className="cj-pod-network-lines" viewBox="0 0 1100 430" preserveAspectRatio="none">
            {linePaths.map((d, i) => <path d={d} key={i} />)}
          </svg>
          {NETWORK.platforms.map((p) => (
            <div className={'cj-pod-pulse-dot ' + p.cls} aria-label={p.name} key={p.key}>
              <i className={p.platCls} role="img" aria-label={p.name} />
            </div>
          ))}
          <div className="cj-pod-center-hub">{chatLogo ? <img src={chatLogo} alt="CJdropshipping" /> : null}</div>
        </div>
        <div className="cj-pod-network-note">
          {NETWORK.notes.map((n, i) => <span key={i}>{n}</span>)}
        </div>
      </div>
    </section>
  );
}

// ========== 10. GLOBAL MAP ==========
function MapSection({ MAP }) {
  return (
    <section className="cj-pod-global-map-section">
      <div className="cj-pod-global-map-container">
        <div className="cj-pod-global-map-head">
          <h2>{MAP.h2}</h2>
          <p>{MAP.desc}</p>
        </div>
        <div className="cj-pod-global-map-layout">
          <div className="cj-pod-global-stats">
            {MAP.stats.map((s, i) => (
              <div className="cj-pod-global-stat" key={i}><strong>{s.n}</strong><span>{s.l}</span></div>
            ))}
          </div>
          <div className="cj-pod-global-map-visual">
            <img src={MAP.image} alt="CJdropshipping global fulfillment map" />
          </div>
        </div>
      </div>
    </section>
  );
}

// ========== 11. AUDIENCE ==========
function AudienceCard({ c }) {
  return (
    <div className="cj-pod-audience-card">
      <div className="cj-pod-audience-img"><img src={c.img} alt={c.h3} /></div>
      <div className="cj-pod-audience-body">
        <div className={'cj-pod-audience-icon' + (c.blue ? ' blue' : '')}>{c.num}</div>
        <div className="cj-pod-audience-content"><h3>{c.h3}</h3><p>{c.p}</p></div>
      </div>
    </div>
  );
}
function AudienceSection({ AUDIENCE }) {
  return (
    <section className="cj-pod-audience-section">
      <div className="cj-pod-audience-bg cj-pod-audience-bg-1" />
      <div className="cj-pod-audience-bg cj-pod-audience-bg-2" />
      <div className="cj-pod-audience-container">
        <div className="cj-pod-audience-head">
          <span className="cj-pod-audience-label">{AUDIENCE.label}</span>
          <h2>{AUDIENCE.h2}</h2>
          <p>{AUDIENCE.desc}</p>
        </div>
        <div className="cj-pod-audience-grid">
          {AUDIENCE.cardsTop.map((c, i) => <AudienceCard c={c} key={i} />)}
          <div className="cj-pod-audience-feature">
            <div className="cj-pod-audience-feature-inner">
              <div className="cj-pod-feature-left"><span>{AUDIENCE.feature.span}</span><h3>{AUDIENCE.feature.h3}</h3></div>
              <div className="cj-pod-feature-right">
                <p>{AUDIENCE.feature.p}</p>
                <a target="_blank" rel="noopener noreferrer" href={REGISTER_HREF} className="cj-pod-audience-btn" data-gtag-element="audience_start_pod">{AUDIENCE.feature.btn}</a>
              </div>
            </div>
          </div>
          {AUDIENCE.cardsBottom.map((c, i) => <AudienceCard c={c} key={i} />)}
        </div>
      </div>
    </section>
  );
}

// ========== 12. TESTIMONIALS ==========
function ReviewCard({ r }) {
  return (
    <div className="cj-pod-review-card">
      <div className="cj-pod-review-top">
        {/* 第19次补充：avatar 去掉雪碧图，改用白色加粗首字母 + 主题色背景 */}
        <span className="cj-pod-review-avatar" role="img" aria-label={r.name}>
          {(r.name || '').trim().charAt(0).toUpperCase()}
        </span>
        <div><strong>{r.name}</strong><span>{r.role}</span></div>
      </div>
      <div className="cj-pod-review-stars">★★★★★</div>
      <p>{'“' + r.text + '”'}</p>
    </div>
  );
}
function TestimonialsSection({ TESTIMONIALS }) {
  return (
    <section className="cj-pod-testimonials-section">
      <div className="cj-pod-testimonials-bg cj-test-bg-1" />
      <div className="cj-pod-testimonials-bg cj-test-bg-2" />
      <div className="cj-pod-testimonials-container">
        <div className="cj-pod-testimonials-head">
          <span className="cj-pod-testimonials-label">{TESTIMONIALS.label}</span>
          <h2>{TESTIMONIALS.h2}</h2>
          <p>{TESTIMONIALS.desc}</p>
        </div>
        <div className="cj-pod-testimonials-marquee">
          <div className="cj-pod-testimonials-row row-left">
            <div className="cj-pod-testimonials-track">
              {TESTIMONIALS.rowL.map((r, i) => <ReviewCard r={r} key={i} />)}
            </div>
          </div>
          <div className="cj-pod-testimonials-row row-right">
            <div className="cj-pod-testimonials-track">
              {TESTIMONIALS.rowR.map((r, i) => <ReviewCard r={r} key={i} />)}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ========== 13. FAQ ==========
function FaqSection({ FAQ }) {
  const [open, setOpen] = useState(0);
  return (
    <section className="cj-pod-faq-split-section">
      <div className="cj-pod-faq-split-bg faq-bg-1" />
      <div className="cj-pod-faq-split-bg faq-bg-2" />
      <div className="cj-pod-faq-split-container">
        <div className="cj-pod-faq-split-left">
          <span className="cj-pod-faq-label">{FAQ.label}</span>
          <h2>{FAQ.h2}</h2>
          <p>{FAQ.desc}</p>
          <div className="cj-pod-faq-info-card">
            <strong>{FAQ.infoT}</strong>
            <span>{FAQ.infoS}</span>
          </div>
          <a target="_blank" rel="noopener noreferrer" href={REGISTER_HREF} className="cj-pod-faq-cta-btn" data-gtag-element="faq_start_pod">{FAQ.btn}</a>
        </div>
        <div className="cj-pod-faq-split-right">
          {FAQ.items.map((it, i) => (
            <div className={'cj-pod-faq-item' + (open === i ? ' active' : '')} key={i}>
              <button className="cj-pod-faq-question" type="button" onClick={() => setOpen(open === i ? -1 : i)}>
                <span>{it.q}</span><em>+</em>
              </button>
              <div className="cj-pod-faq-answer"><p>{it.a}</p></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ========== 14. FINAL CTA ==========
function FinalCta({ CTA }) {
  // 背景图迁移至 OSS：less 无法读运行时 ossHost，改为 JSX 内联 background（CTA.bgImg 已拼 ossHost）
  const bgStyle = CTA.bgImg ? { background: 'url("' + CTA.bgImg + '") center center / cover no-repeat' } : undefined;
  return (
    <section className="cj-pod-final-cta-full" style={bgStyle}>
      <div className="cj-pod-final-cta-overlay" />
      <div className="cj-pod-final-cta-inner">
        <span className="cj-pod-final-cta-label">{CTA.label}</span>
        <h2>{CTA.h2}</h2>
        <p>{CTA.desc}</p>
        <div className="cj-pod-final-cta-buttons">
          <a target="_blank" rel="noopener noreferrer" href={REGISTER_HREF} className="cj-pod-final-btn cj-pod-final-btn-primary" data-gtag-element="final_start_pod">{CTA.btn1}</a>
          <a target="_blank" rel="noopener noreferrer" href={CUSTOM_REQUEST_HREF} className="cj-pod-final-btn cj-pod-final-btn-secondary" data-gtag-element="final_submit_request">{CTA.btn2}</a>
        </div>
      </div>
    </section>
  );
}

/**
 * POD 主页根组件
 * @param {Object} props - withSSR 注入，props.pageData 为 fetch.js 返回数据
 */
function PrintOnDemandHome(props) {
  const pageData = props.pageData || {};
  // 图片迁移至 OSS：home 区块 jpg/svg/cta-bg 拼 ossHost；avatar/logo 用 OSS 雪碧图 CSS
  const ossHost = pageData.ossHost || (typeof OSS_HOST !== 'undefined' ? OSS_HOST : (typeof CDN_HOST !== 'undefined' ? CDN_HOST : ''));
  // 第29次补充：传入 siteName/fullName 供文案 {{name}}/{{fullName}} 插值（CJ/CJdropshipping 改为站点字段）
  const c = useMemo(() => getConstants(ossHost, pageData.siteName, pageData.fullName), [ossHost, pageData.siteName, pageData.fullName]);
  // 第4次补充：CATEGORY 类目/商品来自接口（fetch.js 预取，随首屏 state 下发）
  const categories = pageData.categories || [];

  return (
    <div className="pod" data-gtag-module="print_on_demand">
      {/**
       * 第18次补充：删除 printonDemandHomeLogo 雪碧图及相关逻辑——平台网络区已于第15次改用 resources/platform 的 platform.css 雪碧图，
       * printonDemandHomeLogo 雪碧图已无任何元素引用，故移除其 CSS link（源图/雪碧图插件亦在 static-resource-platform 侧删除）。
       * 第19次补充：评价区 avatar 去掉雪碧图，改用白色加粗首字母 + 主题色背景（见 ReviewCard / .cj-pod-review-avatar），
       * 故同步移除 printonDemandHomeAvatar.css 的 link（页面不再依赖头像雪碧图）。
       */}
      {/* 第15次补充：平台网络节点改用 resources/platform 雪碧图（.platform-* 类），引入 platform.css */}
      <link rel="stylesheet" href={ossHost + '/.sprite/platform.css'} />
      <main className="cj-pod-page">
        <HeroSection HERO={c.HERO} />
        <IntroSection INTRO={c.INTRO} />
        <DiffSection DIFF={c.DIFF} />
        <CategorySection CATEGORY={c.CATEGORY} categories={categories} />
        <BenefitsSection BENEFITS={c.BENEFITS} />
        <ServicesSection SERVICES={c.SERVICES} />
        <BrandSection BRAND={c.BRAND} />
        <ProcessSection PROCESS={c.PROCESS} />
        <NetworkSection NETWORK={c.NETWORK} chatLogo={ossHost + '/config-resource/' + String(pageData.siteName || 'cj').toLowerCase() + '/chat-logo.svg'} />
        <MapSection MAP={c.MAP} />
        <AudienceSection AUDIENCE={c.AUDIENCE} />
        <TestimonialsSection TESTIMONIALS={c.TESTIMONIALS} />
        <FaqSection FAQ={c.FAQ} />
        <FinalCta CTA={c.CTA} />
      </main>
    </div>
  );
}

PrintOnDemandHome.fetch = fetchData;

export default withSSR(PrintOnDemandHome);
